import { IErrorApiResponse } from '@wyz/types';
import { useOnSuccessQuery, UseQueryResult } from '@wyz/utils';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ICustomerOption } from '~/common/types';
import { BaseCustomerSelector } from '~/purchase/components';
import { usePurchaseContext } from '~/purchase/context';
// eslint-disable-next-line @wyz/module/no-relative-import
import { useCustomerSideEffects } from './use-customer-side-effects';

interface SharedCustomerSelectorProps {
  /**
   * Bounded query used to auto select the customer when the user only has one
   * (see useMyCustomersProbe).
   */
  customersProbe: UseQueryResult<Array<ICustomerOption>, IErrorApiResponse>;
  /** Server side search, see useMyCustomersAutocomplete. */
  fetchCustomers: (
    query: string,
    options: { signal?: AbortSignal },
  ) => Promise<Array<ICustomerOption>>;
  onChange: (selectedOption: ICustomerOption | null) => void;
}

function SharedCustomerSelector({
  customersProbe,
  fetchCustomers,
  onChange,
}: SharedCustomerSelectorProps) {
  const [searchParams] = useSearchParams();
  const { state, handlers } = usePurchaseContext();
  const navigate = useNavigate();

  useCustomerSideEffects();

  const customerFromQs = searchParams.get('customer');

  useOnSuccessQuery(customersProbe, (customers) => {
    if (customerFromQs) {
      handlers.setPrimaryCustomerId(customerFromQs);
      return;
    }
    if (!state.selectedPrimaryCustomerId && customers.length === 1) {
      handlers.setPrimaryCustomerId(customers.at(0)?.value);
    }
  });

  // The selected customer is resolved by id by the FunctionalProvider: a
  // failure means the id is unknown or out of the user scope.
  useEffect(() => {
    if (!state.isPrimaryCustomerNotFound) {
      return;
    }
    handlers.clearSelectedCustomers();
    // user asked for a customer that does not exist
    if (customerFromQs) {
      void navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isPrimaryCustomerNotFound]);

  return (
    <BaseCustomerSelector
      key={state.selectedPrimaryCustomerId}
      isLoading={
        customersProbe.isProcessing || state.loading.isLoadingPrimaryCustomer
      }
      isDisabled={state.loading.isLoadingPrimaryCustomer}
      value={
        state.selectedPrimaryCustomerId && state.primaryCustomer
          ? {
              label: `${state.primaryCustomer.name} (${state.primaryCustomer.code})`,
              value: state.primaryCustomer.id,
              name: state.primaryCustomer.name,
              city: state.primaryCustomer.deliveryInformation?.city ?? '',
              zipCode: state.primaryCustomer.deliveryInformation?.zipCode ?? '',
            }
          : undefined
      }
      fetcher={(query, options) =>
        fetchCustomers(query, { signal: options.signal })
      }
      defaultOptionsFetcher={(options) =>
        fetchCustomers('', { signal: options.signal })
      }
      onChange={onChange}
      name='main-customer'
    />
  );
}

export default SharedCustomerSelector;
